import { useLayoutEffect, useState } from 'react';

/** Matches the `duration-200` used by the collapsible reveals across the event and SAFE forms. */
const REVEAL_DURATION_MS = 200;

/**
 * A `grid-rows-[0fr]` → `[1fr]` reveal needs its body clipped to read as a reveal, but a clipped
 * body also cuts off any popup a field inside it opens — a combobox list, a date picker calendar —
 * at the card's edge.
 *
 * So the clip is only worn while the section is collapsed or animating. It goes back on before the
 * height starts shrinking (a layout effect, so no unclipped frame ever paints) and comes off once
 * the section has finished opening.
 *
 * @param isOpen whether the section is currently revealed.
 * @returns whether the body should carry `overflow-hidden` right now.
 */
export const useRevealClip = (isOpen: boolean): boolean => {
  const [isClipped, setClipped] = useState(!isOpen);

  useLayoutEffect(() => {
    if (!isOpen) {
      setClipped(true);
      return;
    }

    const timeout = setTimeout(() => setClipped(false), REVEAL_DURATION_MS);

    return () => clearTimeout(timeout);
  }, [isOpen]);

  return isClipped;
};
